"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  Link as ChakraLink,
  Container,
  Card,
  CardBody,
  Image,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { supabase } from "../supabaseClient";
import { useNavigate, Link, useLocation } from "react-router-dom";

export default function SignIn() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const location = useLocation();
  const successMessage = location.state?.message;

  useEffect(() => {
    if (successMessage) {
      // Clear the message from location state after showing it
      window.history.replaceState({}, document.title);
    }
  }, [successMessage]);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        navigate("/dashboard");
      }
    } catch (error: any) {
      setError(error.error_description || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody p={8}>
          <VStack spacing={6}>
            <VStack spacing={4} textAlign="center">
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI Logo"
                maxH="200px"
                objectFit="contain"
              />
              <Heading size="lg" color="gray.700">
                Welcome back
              </Heading>
              <Text color="gray.600">Sign in to your account to continue</Text>
            </VStack>

            {error && (
              <Alert status="error" borderRadius="md" textColor={"red.600"}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert status="success" borderRadius="md" textColor={"green.600"}>
                <AlertIcon />
                {successMessage}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSignIn} w="100%">
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.600">Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    bg="gray.50"
                    _placeholder={{ color: "gray.500" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      bg="gray.50"
                      _placeholder={{ color: "gray.500" }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        size="sm"
                        color={"primary.500"}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="primary"
                  size="lg"
                  w="100%"
                  isLoading={loading}
                  loadingText="Signing in..."
                >
                  Sign In
                </Button>
              </VStack>
            </Box>

            <VStack spacing={3}>
              <ChakraLink as={Link} to="/forgot-password" color="primary.600">
                Forgot your password?
              </ChakraLink>
              <Text fontSize="sm" color="gray.600">
                Don't have an account?{" "}
                <ChakraLink as={Link} to="/signup" color="primary.600">
                  Sign Up
                </ChakraLink>
              </Text>
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
}
